import type { PolicyImpactLevel, PolicyReviewStatus } from "./enums";
import { err, ok, type Result } from "./result";

import { cleanNullableText, type NullableText } from "./text";
import { dueDateLabel, formatAuDate } from "./time";

export type PolicyReviewDecisionError = { readonly kind: "missingNoChangeReason" };
export type OpenPolicyReviewStatus = Exclude<PolicyReviewStatus, "updated" | "notAffected">;
export type ClosedPolicyReviewStatus = Extract<PolicyReviewStatus, "updated" | "notAffected">;

export type PolicyReviewStatusCommand =
  | {
      readonly kind: "markUpdated";
      readonly decisionNote?: NullableText;
    }
  | {
      readonly kind: "closeNoPolicyChange";
      readonly decisionNote: NullableText;
    }
  | {
      readonly kind: "reopen";
      readonly status: OpenPolicyReviewStatus;
    };

export type PolicyReviewActionState = "idle" | "saving" | "saved" | "error";
export type PolicySuggestionState = "idle" | "generating" | "generated" | "error";

export type PolicySuggestionWorkflowState = {
  readonly suggestionState: PolicySuggestionState;
  readonly suggestionError: string | null;
};

export const emptyPolicySuggestionWorkflowState = (): PolicySuggestionWorkflowState => ({
  suggestionState: "idle",
  suggestionError: null,
});

export type PolicySuggestionWorkflowAction =
  | { readonly type: "reset" }
  | { readonly type: "generationStarted" }
  | { readonly type: "generationSucceeded" }
  | { readonly type: "generationFailed"; readonly error: string };

export const nextPolicySuggestionWorkflowState = (
  _current: PolicySuggestionWorkflowState,
  action: PolicySuggestionWorkflowAction,
): PolicySuggestionWorkflowState => {
  switch (action.type) {
    case "reset":
      return { suggestionState: "idle", suggestionError: null };
    case "generationStarted":
      return { suggestionState: "generating", suggestionError: null };
    case "generationSucceeded":
      return { suggestionState: "generated", suggestionError: null };
    case "generationFailed":
      return { suggestionState: "error", suggestionError: action.error };
  }
};

export type PolicyReviewDraftState = {
  readonly body: string;
  readonly saveState: PolicyReviewActionState;
  readonly saveError: string | null;
};

export const emptyPolicyReviewDraftState = (): PolicyReviewDraftState => ({
  body: "",
  saveState: "idle",
  saveError: null,
});

export type PolicyReviewDraftAction =
  | { readonly type: "sectionLoaded"; readonly body: string }
  | { readonly type: "bodyInput"; readonly body: string }
  | { readonly type: "saveStarted" }
  | { readonly type: "saveSucceeded" }
  | { readonly type: "saveFailed"; readonly error: string };

export const nextPolicyReviewDraftState = (
  current: PolicyReviewDraftState,
  action: PolicyReviewDraftAction,
): PolicyReviewDraftState => {
  switch (action.type) {
    case "sectionLoaded":
      return { body: action.body, saveState: "idle", saveError: null };
    case "bodyInput":
      return { ...current, body: action.body, saveState: "idle", saveError: null };
    case "saveStarted":
      return { ...current, saveState: "saving", saveError: null };
    case "saveSucceeded":
      return { ...current, saveState: "saved", saveError: null };
    case "saveFailed":
      return { ...current, saveState: "error", saveError: action.error };
  }
};

export type PolicyReviewNoChangeDecisionState = {
  readonly reason: string;
  readonly decisionState: PolicyReviewActionState;
  readonly decisionError: string | null;
};

export const emptyPolicyReviewNoChangeDecisionState = (): PolicyReviewNoChangeDecisionState => ({
  reason: "",
  decisionState: "idle",
  decisionError: null,
});

export type PolicyReviewNoChangeDecisionAction =
  | { readonly type: "reviewLoaded"; readonly reason: string }
  | { readonly type: "reasonInput"; readonly reason: string }
  | { readonly type: "statusReset" }
  | { readonly type: "saveStarted" }
  | { readonly type: "saveSucceeded" }
  | { readonly type: "saveFailed"; readonly error: string };

export const nextPolicyReviewNoChangeDecisionState = (
  current: PolicyReviewNoChangeDecisionState,
  action: PolicyReviewNoChangeDecisionAction,
): PolicyReviewNoChangeDecisionState => {
  switch (action.type) {
    case "reviewLoaded":
      return { reason: action.reason, decisionState: "idle", decisionError: null };
    case "reasonInput":
      return { ...current, reason: action.reason, decisionState: "idle", decisionError: null };
    case "statusReset":
      return { ...current, decisionState: "idle", decisionError: null };
    case "saveStarted":
      return { ...current, decisionState: "saving", decisionError: null };
    case "saveSucceeded":
      return { ...current, decisionState: "saved", decisionError: null };
    case "saveFailed":
      return { ...current, decisionState: "error", decisionError: action.error };
  }
};

export type PolicyReviewDraftView = {
  readonly changed: boolean;
  readonly canSave: boolean;
  readonly message: string;
  readonly tone: "muted" | "saved" | "error";
};

export type PolicyReviewOwnerSummaryPolicy = {
  readonly ownerName?: string | null;
  readonly department?: string | null;
};

export type PolicyReviewOwnerSummaryReview = {
  readonly dueAt: number | null;
  readonly matchedAt: number;
};

export type PolicyReviewOwnerSummaryInput = {
  readonly policy: PolicyReviewOwnerSummaryPolicy | null;
  readonly review: PolicyReviewOwnerSummaryReview;
  readonly nowMs: number;
};

export type PolicyReviewOwnerSummaryRowKey = "owner" | "department" | "due" | "matched";

export type PolicyReviewOwnerSummaryRow = {
  readonly key: PolicyReviewOwnerSummaryRowKey;
  readonly label: string;
  readonly value: string;
};

export const hasSuggestedPolicyText = (text: string): boolean =>
  text.trim() !== "";

export const policyReviewOwnerSummaryRows = ({
  policy,
  review,
  nowMs,
}: PolicyReviewOwnerSummaryInput): PolicyReviewOwnerSummaryRow[] => [
  { key: "owner", label: "Owner", value: policy?.ownerName ?? "Unassigned" },
  { key: "department", label: "Department", value: policy?.department ?? "Document" },
  { key: "due", label: "Due", value: dueDateLabel(review.dueAt, nowMs) },
  { key: "matched", label: "Matched", value: formatAuDate(review.matchedAt) },
];

export const policyReviewDraftView = (input: {
  readonly savedBody: string | null;
  readonly draftBody: string;
  readonly saveState: PolicyReviewActionState;
  readonly saveError: string | null;
}): PolicyReviewDraftView => {
  const changed = input.savedBody !== null && input.draftBody !== input.savedBody;
  const canSave =
    input.savedBody !== null &&
    changed &&
    input.draftBody.trim() !== "" &&
    input.saveState !== "saving";

  if (input.saveState === "saved") {
    return {
      changed,
      canSave,
      message: "Document clause saved and review marked updated.",
      tone: "saved",
    };
  }
  if (input.saveState === "error") {
    return {
      changed,
      canSave,
      message: input.saveError ?? "Could not save document update.",
      tone: "error",
    };
  }

  return {
    changed,
    canSave,
    message: changed ? "Unsaved document wording changes." : "No wording changes yet.",
    tone: "muted",
  };
};

export const canCloseReviewAsNoPolicyChange = (input: {
  readonly reviewStatus: PolicyReviewStatus | null | undefined;
  readonly decisionState: PolicyReviewActionState;
  readonly noChangeReason: string;
}): boolean =>
  input.reviewStatus !== null &&
  input.reviewStatus !== undefined &&
  input.reviewStatus !== "notAffected" &&
  input.decisionState !== "saving" &&
  cleanDecisionNote(input.noChangeReason) !== null;

export const canGeneratePolicySuggestion = (input: {
  readonly hasCompany: boolean;
  readonly hasReview: boolean;
  readonly hasSelectedSection: boolean;
  readonly suggestionState: PolicySuggestionState;
}): boolean =>
  input.hasCompany &&
  input.hasReview &&
  input.hasSelectedSection &&
  input.suggestionState !== "generating";

export const policyReviewDecisionErrorMessage = (
  error: PolicyReviewDecisionError,
): string => {
  switch (error.kind) {
    case "missingNoChangeReason":
      return "Add a short reason before closing this review as no document change.";
  }
};

export type PolicyReviewStatusPatch = {
  readonly status: PolicyReviewStatus;
  readonly resolvedAt: number | null;
  readonly decisionNote?: string | null;
};

export type PolicyReviewOpenCommand<MatchId extends string = string> =
  | { readonly kind: "open"; readonly matchId: MatchId }
  | { readonly kind: "markInReviewAndOpen"; readonly matchId: MatchId; readonly status: Extract<PolicyReviewStatus, "inReview"> };

export type PolicyReviewOpenCommandInput<MatchId extends string = string> = {
  readonly matchId: MatchId;
  readonly currentStatus: PolicyReviewStatus;
};

export type PolicyReviewStartCommand<MatchId extends string = string> =
  | { readonly kind: "ready"; readonly matchId: MatchId; readonly status: Extract<PolicyReviewStatus, "inReview"> }
  | { readonly kind: "blocked" };

export type PolicyReviewStartCommandInput<MatchId extends string = string> = {
  readonly matchId: MatchId;
  readonly currentStatus: PolicyReviewStatus;
};

export type PolicyReviewNoChangeCloseCommand<MatchId extends string = string> = {
  readonly matchId: MatchId;
  readonly status: Extract<PolicyReviewStatus, "notAffected">;
  readonly decisionNote: string;
};

export type PolicyReviewNoChangeCloseCommandInput<MatchId extends string = string> = {
  readonly matchId: MatchId;
  readonly decisionNote: NullableText;
};

export type PolicySuggestionStatusPatch = {
  readonly status: Extract<PolicyReviewStatus, "inReview" | "updated">;
  readonly resolvedAt: number | null;
};

export type PolicySuggestionStatusInput = {
  readonly currentStatus: PolicyReviewStatus;
  readonly currentResolvedAt: number | null;
};

export type PolicySuggestionPatch<SectionId extends string = string> = {
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText: string;
  readonly matchedTerms: string[];
  readonly policySectionIds: SectionId[];
  readonly status: Extract<PolicyReviewStatus, "inReview" | "updated">;
  readonly resolvedAt: number | null;
};

export type PolicySuggestionPatchInput<SectionId extends string = string> = {
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText: string;
  readonly matchedTerms: readonly string[];
  readonly currentPolicySectionIds: readonly SectionId[] | null | undefined;
  readonly policySectionId: SectionId;
  readonly currentStatus: PolicyReviewStatus;
  readonly currentResolvedAt: number | null;
};

export type GeneratedPolicySuggestion = {
  readonly affected: boolean;
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText: string;
  readonly matchedTerms: readonly string[];
};

export type CleanPolicySuggestion = {
  readonly affected: boolean;
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText: string;
  readonly matchedTerms: string[];
};

export type PolicyImpactMatchFields<
  CompanyId extends string = string,
  ScopeId extends string = string,
  PolicyId extends string = string,
  UpdateLogId extends string = string,
  LegislationId extends string = string,
  SectionId extends string = string,
> = {
  readonly companyId: CompanyId;
  readonly businessScopeId: ScopeId | null;
  readonly policyId: PolicyId;
  readonly updateLogId: UpdateLogId;
  readonly legislationId: LegislationId;
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText: string | null;
  readonly matchedTerms: string[];
  readonly policySectionIds: SectionId[];
  readonly dueAt: number | null;
};

export type PolicyImpactMatchInput<
  CompanyId extends string = string,
  ScopeId extends string = string,
  PolicyId extends string = string,
  UpdateLogId extends string = string,
  LegislationId extends string = string,
  SectionId extends string = string,
> = {
  readonly companyId: CompanyId;
  readonly businessScopeId?: ScopeId | null;
  readonly policyId: PolicyId;
  readonly updateLogId: UpdateLogId;
  readonly legislationId: LegislationId;
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText?: string | null;
  readonly matchedTerms: readonly string[];
  readonly policySectionIds: readonly SectionId[];
  readonly dueAt: number | null;
};

export type PolicyImpactMatchInsert<
  CompanyId extends string = string,
  ScopeId extends string = string,
  PolicyId extends string = string,
  UpdateLogId extends string = string,
  LegislationId extends string = string,
  SectionId extends string = string,
> = PolicyImpactMatchFields<CompanyId, ScopeId, PolicyId, UpdateLogId, LegislationId, SectionId> & {
  readonly status: PolicyReviewStatus;
  readonly matchedAt: number;
  readonly resolvedAt: null;
};

export type PolicyImpactMatchPatch<
  ScopeId extends string = string,
  LegislationId extends string = string,
  SectionId extends string = string,
> = {
  readonly businessScopeId: ScopeId | null;
  readonly legislationId: LegislationId;
  readonly impactLevel: PolicyImpactLevel;
  readonly rationale: string;
  readonly suggestedAction: string;
  readonly proposedPolicyText: string | null;
  readonly matchedTerms: string[];
  readonly policySectionIds: SectionId[];
  readonly dueAt: number | null;
};

export const cleanDecisionNote = (note: NullableText): string | null => cleanNullableText(note);

export const reviewStatusIsClosed = (status: PolicyReviewStatus): status is ClosedPolicyReviewStatus =>
  status === "updated" || status === "notAffected";

export const policyReviewStatusCommandFor = ({
  status,
  decisionNote,
}: {
  readonly status: PolicyReviewStatus;
  readonly decisionNote?: NullableText;
}): PolicyReviewStatusCommand => {
  switch (status) {
    case "updated":
      return { kind: "markUpdated", decisionNote };
    case "notAffected":
      return { kind: "closeNoPolicyChange", decisionNote };
    case "pending":
    case "inReview":
      return { kind: "reopen", status };
  }
};

export const policyReviewOpenCommand = <MatchId extends string>({
  matchId,
  currentStatus,
}: PolicyReviewOpenCommandInput<MatchId>): PolicyReviewOpenCommand<MatchId> =>
  currentStatus === "pending"
    ? { kind: "markInReviewAndOpen", matchId, status: "inReview" }
    : { kind: "open", matchId };

export const policyReviewStartCommand = <MatchId extends string>({
  matchId,
  currentStatus,
}: PolicyReviewStartCommandInput<MatchId>): PolicyReviewStartCommand<MatchId> =>
  currentStatus === "pending"
    ? { kind: "ready", matchId, status: "inReview" }
    : { kind: "blocked" };

export const policyReviewNoChangeCloseCommand = <MatchId extends string>({
  matchId,
  decisionNote,
}: PolicyReviewNoChangeCloseCommandInput<MatchId>): Result<
  PolicyReviewNoChangeCloseCommand<MatchId>,
  PolicyReviewDecisionError
> => {
  const note = cleanDecisionNote(decisionNote);
  if (note === null) return err({ kind: "missingNoChangeReason" });
  return ok({ matchId, status: "notAffected", decisionNote: note });
};

export const policySuggestionStatusPatch = (
  input: PolicySuggestionStatusInput,
): PolicySuggestionStatusPatch => {
  if (input.currentStatus === "updated") {
    return { status: "updated", resolvedAt: input.currentResolvedAt };
  }
  return { status: "inReview", resolvedAt: null };
};

export const policySectionIdsWithSuggestion = <SectionId extends string>(
  current: readonly SectionId[] | null | undefined,
  sectionId: SectionId,
): SectionId[] => current?.includes(sectionId) === true ? [...current] : [...(current ?? []), sectionId];

export const uniquePolicySuggestionTerms = (
  values: readonly string[],
  limit = 12,
): string[] => {
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const value of values) {
    const item = value.trim().toLowerCase();
    if (item === "" || seen.has(item)) continue;
    seen.add(item);
    cleaned.push(item);
    if (cleaned.length >= limit) break;
  }
  return cleaned;
};

const trimPolicySuggestionStorage = (value: string, maxLength: number): string => {
  const clean = value.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  if (clean.length <= maxLength) return clean;
  return clean.slice(0, maxLength - 1).trimEnd() + "...";
};

export const cleanGeneratedPolicySuggestion = (
  value: GeneratedPolicySuggestion,
  currentPolicyText: string,
): CleanPolicySuggestion => {
  const proposedPolicyText = value.proposedPolicyText.trim() || currentPolicyText.trim();
  return {
    affected: value.affected,
    impactLevel: value.impactLevel,
    rationale: trimPolicySuggestionStorage(value.rationale, 900),
    suggestedAction: trimPolicySuggestionStorage(value.suggestedAction, 700),
    proposedPolicyText: trimPolicySuggestionStorage(proposedPolicyText, 5000),
    matchedTerms: uniquePolicySuggestionTerms(value.matchedTerms, 8),
  };
};

export const preparePolicySuggestionPatch = <SectionId extends string>(
  input: PolicySuggestionPatchInput<SectionId>,
): PolicySuggestionPatch<SectionId> => ({
  impactLevel: input.impactLevel,
  rationale: input.rationale.trim(),
  suggestedAction: input.suggestedAction.trim(),
  proposedPolicyText: input.proposedPolicyText.trim(),
  matchedTerms: uniquePolicySuggestionTerms(input.matchedTerms),
  policySectionIds: policySectionIdsWithSuggestion(
    input.currentPolicySectionIds,
    input.policySectionId,
  ),
  ...policySuggestionStatusPatch({
    currentStatus: input.currentStatus,
    currentResolvedAt: input.currentResolvedAt,
  }),
});

export const preparePolicyImpactMatchFields = <
  CompanyId extends string,
  ScopeId extends string,
  PolicyId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  SectionId extends string,
>(
  input: PolicyImpactMatchInput<CompanyId, ScopeId, PolicyId, UpdateLogId, LegislationId, SectionId>,
): PolicyImpactMatchFields<CompanyId, ScopeId, PolicyId, UpdateLogId, LegislationId, SectionId> => ({
  companyId: input.companyId,
  businessScopeId: input.businessScopeId ?? null,
  policyId: input.policyId,
  updateLogId: input.updateLogId,
  legislationId: input.legislationId,
  impactLevel: input.impactLevel,
  rationale: input.rationale.trim(),
  suggestedAction: input.suggestedAction.trim(),
  proposedPolicyText: input.proposedPolicyText?.trim() || null,
  matchedTerms: uniquePolicySuggestionTerms(input.matchedTerms),
  policySectionIds: [...input.policySectionIds],
  dueAt: input.dueAt,
});

export const preparePolicyImpactMatchInsert = <
  CompanyId extends string,
  ScopeId extends string,
  PolicyId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  SectionId extends string,
>(
  fields: PolicyImpactMatchFields<CompanyId, ScopeId, PolicyId, UpdateLogId, LegislationId, SectionId>,
  status: PolicyReviewStatus,
  now: number,
): PolicyImpactMatchInsert<CompanyId, ScopeId, PolicyId, UpdateLogId, LegislationId, SectionId> => ({
  ...fields,
  status,
  matchedAt: now,
  resolvedAt: null,
});

export const policyImpactMatchRefreshPatch = <
  ScopeId extends string,
  LegislationId extends string,
  SectionId extends string,
>(
  fields: Pick<PolicyImpactMatchFields<string, ScopeId, string, string, LegislationId, SectionId>,
    "businessScopeId" | "legislationId" | "impactLevel" | "rationale" | "suggestedAction" | "proposedPolicyText" | "matchedTerms" | "policySectionIds" | "dueAt">,
): PolicyImpactMatchPatch<ScopeId, LegislationId, SectionId> => ({
  businessScopeId: fields.businessScopeId,
  legislationId: fields.legislationId,
  impactLevel: fields.impactLevel,
  rationale: fields.rationale,
  suggestedAction: fields.suggestedAction,
  proposedPolicyText: fields.proposedPolicyText,
  matchedTerms: [...fields.matchedTerms],
  policySectionIds: [...fields.policySectionIds],
  dueAt: fields.dueAt,
});

export const prepareReviewStatusPatch = ({
  command,
  now,
}: {
  readonly command: PolicyReviewStatusCommand;
  readonly now: number;
}): Result<PolicyReviewStatusPatch, PolicyReviewDecisionError> => {
  switch (command.kind) {
    case "markUpdated":
      return ok({
        status: "updated",
        resolvedAt: now,
        decisionNote: cleanDecisionNote(command.decisionNote),
      });
    case "closeNoPolicyChange": {
      const note = cleanDecisionNote(command.decisionNote);
      if (note === null) return err({ kind: "missingNoChangeReason" });
      return ok({
        status: "notAffected",
        resolvedAt: now,
        decisionNote: note,
      });
    }
    case "reopen":
      return ok({
        status: command.status,
        resolvedAt: null,
        decisionNote: null,
      });
  }
};
